import { Gift, Heart, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import useScrollLock from '@/hooks/useScrollLock';

export default function DeckModal({ 
  isOpen, 
  onClose, 
  deck, 
  products, 
  user,
  onAddToWishlist,
  onAddToKeepsake
}) {
  // Lock the viewport scroll while the modal is open (html-level lock,
  // body-only locks don't work because html has overflow-x: clip).
  useScrollLock(isOpen);

  if (!isOpen) return null;

  // Render through a portal to <body>. The product grid has a CSS transform
  // (animation), which makes `position: fixed` resolve relative to the grid
  // instead of the viewport, that pushed the modal far down the page
  // ("jumps to the bottom"). Portaling to body escapes that containing block.
  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl p-4 sm:p-8"
        style={{
          background: 'linear-gradient(135deg, #2D4354 0%, #534145 100%)',
          border: '2px solid #FED7A5',
          boxShadow: '0 8px 32px rgba(45, 67, 84, 0.4)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-all hover:bg-white/10"
          style={{ color: '#FED7A5' }}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="mb-4">
            <h2 
              className="text-3xl font-bold mb-2"
              style={{
                fontFamily: 'Playfair Display, serif',
                color: '#FED7A5'
              }}
            >
              {deck.title}
            </h2>
            <p className="text-base" style={{ color: '#E5E5E5' }}>
              {deck.description}
            </p>
          </div>

          {/* Action Buttons - ALWAYS SHOW FOR TESTING */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onAddToWishlist();
              }}
              variant="outline"
              className="flex items-center gap-2"
              style={{
                borderColor: '#FED7A5',
                color: '#FED7A5',
                background: 'rgba(254, 215, 165, 0.1)',
                padding: '8px 16px'
              }}
            >
              <Heart className="w-4 h-4" />
              Add All to Wishlist
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onAddToKeepsake();
              }}
              variant="outline"
              className="flex items-center gap-2"
              style={{
                borderColor: '#FED7A5',
                color: '#FED7A5',
                background: 'rgba(254, 215, 165, 0.1)',
                padding: '8px 16px'
              }}
            >
              <Gift className="w-4 h-4" />
              Add All to Keepsake
            </Button>
          </div>
        </div>

        {/* Products Grid - 2-up on mobile so cards stay compact (no giant
            single-column cards with dead space), 3-up on larger screens. */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-x-4 sm:gap-y-6">
          {products.map((product) => (
            <ProductCard 
              key={product.id}
              product={product} 
              user={user}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
